// 区分マスタ(AreaDivision / MaterialDivision)複製の複製計画を組み立てる純関数群。
// MasterCrudPage.confirmDuplicateDivision から呼ばれる。
//
// 設計上の要点:
// - ID 写像はコレクション別に持つ。全コレクション共有の Map<旧ID, 新ID> は、別マスタ同士で
//   旧IDが衝突すると後勝ち上書きされ、外部キーが無関係なマスタの新IDへ誤配線される(実バグとして検出済み)。
// - 外部キーの再配線は「参照元コレクション × 外部キー名 → 参照先コレクション」の明示対応表でのみ行う。
//   `_id` 接尾辞などの命名規則推測は、類似名の追加で誤爆するため使わない。
// - 採番(nextIdFor)は非同期のため関数外(呼び出し側)で行い、結果を注入する。本モジュールは副作用を持たない。

import { ExtendedDB } from "../../../types/extendedDB";
import { readProp } from "../../../util/typed";

export type PartitionKey = "area_division_id" | "material_division_id";

// 複製対象コレクションの行(動的キー駆動のため record 形。実体は各コレクションの行)。
export type DuplicableRow = Record<string, unknown>;

// 参照元コレクション → { 外部キー名 → 参照先コレクション }。
// 「複製対象になり得るコレクション(区分パーティションキーを持つマスタ)」同士の参照のみ列挙する。
// 複製対象外(clients 等、区分に紐づかない共通マスタ)への外部キーは再配線せず共有参照のまま維持する。
// 新しい区分配下マスタ・外部キーを追加したら、この表とユニットテストを同時に更新すること。
const DUPLICATION_FK_TARGETS: Partial<Record<keyof ExtendedDB, Record<string, keyof ExtendedDB>>> = {
  workSubtypes: {
    work_type_id: "workTypes",
    primary_work_detail_id: "workDetails",
  },
  workDetails: {
    work_subtype_id: "workSubtypes",
    mix_ratio_definition_id: "mixRatioDefinitions",
  },
  qualityMeasurementItems: {
    material_id: "materials",
    work_type_id: "workTypes",
    work_subtype_id: "workSubtypes",
    work_detail_id: "workDetails",
  },
  materialDetails: {
    material_id: "materials",
  },
  potLifeSpecs: {
    material_id: "materials",
  },
  mixRatioDefinitions: {
    material_id: "materials",
    stirring_method_id: "stirringMethods",
    construction_method_id: "constructionMethods",
  },
  mixRatioDetails: {
    material_id: "materials",
    mix_ratio_definition_id: "mixRatioDefinitions",
    material_detail_id: "materialDetails",
  },
};

function readNumberField(row: DuplicableRow, key: string): number | null {
  const value = readProp(row, key);
  if (typeof value === "number") {
    return value;
  }
  return null;
}

function isAliveRow(row: DuplicableRow): boolean {
  return readProp(row, "deleted_at") == null;
}

// 指定区分に紐づく複製対象行(alive のみ)をコレクション別に収集する。
// 採番が必要なコレクション(=1行以上ある)だけをキーに含める。
export function collectDivisionChildRows(
  collections: ReadonlyArray<readonly [keyof ExtendedDB, ReadonlyArray<DuplicableRow>]>,
  partitionKey: PartitionKey,
  sourceDivisionId: number,
): Map<keyof ExtendedDB, DuplicableRow[]> {
  const matchingByCollection = new Map<keyof ExtendedDB, DuplicableRow[]>();
  for (const [key, rows] of collections) {
    const matching = rows.filter((row) => readNumberField(row, partitionKey) === sourceDivisionId && isAliveRow(row));
    if (matching.length > 0) {
      matchingByCollection.set(key, matching);
    }
  }
  return matchingByCollection;
}

export type DivisionDuplicationInput = {
  // collectDivisionChildRows の結果
  matchingByCollection: ReadonlyMap<keyof ExtendedDB, ReadonlyArray<DuplicableRow>>;
  // コレクションごとの新ID開始値(呼び出し側が nextIdFor で採番して注入する)
  nextIdByCollection: ReadonlyMap<keyof ExtendedDB, number>;
  partitionKey: PartitionKey;
  newDivisionId: number;
  nowMs: number;
};

// 複製行(ID 再採番・区分付け替え・外部キー再配線済み)をコレクション別に組み立てる。
export function buildDivisionDuplication(input: DivisionDuplicationInput): Map<keyof ExtendedDB, DuplicableRow[]> {
  // 第1段: コレクション別に新IDを割り当て、コレクション別 ID 写像を作る。
  const idMapByCollection = new Map<keyof ExtendedDB, Map<number, number>>();
  const copiesByCollection = new Map<keyof ExtendedDB, DuplicableRow[]>();
  for (const [key, matching] of input.matchingByCollection) {
    const startId = input.nextIdByCollection.get(key);
    if (startId === undefined) {
      throw new Error(`コレクション ${String(key)} の新ID採番結果が渡されていません(データ不整合)。`);
    }
    const idMap = new Map<number, number>();
    const copies: DuplicableRow[] = [];
    let currentId = startId;
    for (const orig of matching) {
      const newId = currentId;
      currentId += 1;
      const origId = readNumberField(orig, "id");
      if (origId !== null) {
        idMap.set(origId, newId);
      }
      copies.push({ ...orig, id: newId, [input.partitionKey]: input.newDivisionId, createdAt: input.nowMs, updatedAt: input.nowMs, deleted_at: null });
    }
    idMapByCollection.set(key, idMap);
    copiesByCollection.set(key, copies);
  }

  // 第2段: 明示対応表に載っている外部キーだけを、参照先コレクションの ID 写像で再配線する。
  // 参照先が今回の複製対象に含まれない、または写像に旧IDが無い場合は元の値を維持する。
  for (const [key, copies] of copiesByCollection) {
    const fkTargets = DUPLICATION_FK_TARGETS[key];
    if (fkTargets === undefined) {
      continue;
    }
    for (const copy of copies) {
      for (const [fkName, targetKey] of Object.entries(fkTargets)) {
        const targetIdMap = idMapByCollection.get(targetKey);
        if (targetIdMap === undefined) {
          continue;
        }
        const fkValue = readNumberField(copy, fkName);
        if (fkValue === null) {
          continue;
        }
        const rewired = targetIdMap.get(fkValue);
        if (rewired !== undefined) {
          copy[fkName] = rewired;
        }
      }
    }
  }
  return copiesByCollection;
}
